import { useState, useEffect, useCallback } from 'react';
import {
  getSessionTimeline,
  getSessionDetail,
  SessionTimeline,
  toDateRangeParams
} from '../api/invoiceApi';
import { DateRange } from '../components/invoices/DateRangeSelector';

interface UseSessionTimelineOptions {
  /** Table ID to fetch sessions for */
  tableId: number | null;
  /** Date range for filtering sessions */
  dateRange: DateRange;
  /** Whether to auto-fetch on mount/change */
  autoFetch?: boolean;
}

interface UseSessionTimelineReturn {
  /** List of sessions for the table */
  sessions: SessionTimeline[];
  /** Currently selected session for detail view */
  selectedSession: SessionTimeline | null;
  /** Whether sessions are loading */
  loading: boolean;
  /** Whether session detail is loading */
  loadingDetail: boolean;
  /** Error message if any */
  error: string | null;
  /** Fetch sessions for the table */
  fetchSessions: () => Promise<void>;
  /** Fetch detail for a specific session */
  fetchSessionDetail: (sessionId: number) => Promise<SessionTimeline | null>;
  /** Select a session */
  selectSession: (session: SessionTimeline | null) => void;
  /** Clear selected session */
  clearSelection: () => void;
  /** Refetch all data */
  refresh: () => Promise<void>;
}

/**
 * Hook for managing session timeline data for a specific table.
 * Handles fetching, caching, and selection of sessions.
 */
export function useSessionTimeline(options: UseSessionTimelineOptions): UseSessionTimelineReturn {
  const { tableId, dateRange, autoFetch = true } = options;

  // Data state
  const [sessions, setSessions] = useState<SessionTimeline[]>([]);
  const [selectedSession, setSelectedSession] = useState<SessionTimeline | null>(null);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch sessions for the table
   */
  const fetchSessions = useCallback(async () => {
    if (!tableId) {
      setSessions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = toDateRangeParams(dateRange.startDate, dateRange.endDate);
      const data = await getSessionTimeline(tableId, params);

      // Sort sessions by start time (newest first)
      const sorted = [...data].sort((a, b) =>
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
      );

      setSessions(sorted);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch sessions';
      setError(message);
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [tableId, dateRange]);

  /**
   * Fetch detail for a specific session
   */
  const fetchSessionDetail = useCallback(async (sessionId: number): Promise<SessionTimeline | null> => {
    // First check if we already have it in the sessions list
    const existing = sessions.find(s => s.id === sessionId);
    if (existing) {
      setSelectedSession(existing);
      return existing;
    }

    setLoadingDetail(true);

    try {
      const detail = await getSessionDetail(sessionId);
      setSelectedSession(detail);
      return detail;
    } catch (err: any) {
      console.error('Error fetching session detail:', err);
      return null;
    } finally {
      setLoadingDetail(false);
    }
  }, [sessions]);

  /**
   * Select a session
   */
  const selectSession = useCallback((session: SessionTimeline | null) => {
    setSelectedSession(session);
  }, []);

  /**
   * Clear selected session
   */
  const clearSelection = useCallback(() => {
    setSelectedSession(null);
  }, []);

  /**
   * Refresh all data
   */
  const refresh = useCallback(async () => {
    await fetchSessions();
  }, [fetchSessions]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (autoFetch && tableId) {
      fetchSessions();
    }
  }, [fetchSessions, autoFetch, tableId]);

  // Clear sessions when table changes
  useEffect(() => {
    if (!tableId) {
      setSessions([]);
      setSelectedSession(null);
    }
  }, [tableId]);

  return {
    sessions,
    selectedSession,
    loading,
    loadingDetail,
    error,
    fetchSessions,
    fetchSessionDetail,
    selectSession,
    clearSelection,
    refresh
  };
}

export default useSessionTimeline;
